<template>
  <div class="list-product-categories-page">
    <PageHeader
      :title="t('myShopData.product_categories.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="ProductCategories">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="findPermission('my_data.export')"
          :show-export-all-button="true"
          :export-config="{ entityType: 'product_categories' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: category_info (id, name, dates) -->
          <template #cell-category_info="{ data }">
            <div class="category-info-cell">
              <div class="category-row">
                <span
                  v-if="cleanStr(data.name)"
                  class="category-name"
                >
                  {{ cleanStr(data.name) }}
                </span>
                <span
                  v-else
                  class="category-name category-name--empty"
                >
                  {{ t('myShopData.product_categories.noName') }}
                </span>
                <SpmBadge
                  :label="`#${data.category_id}`"
                  :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
                  size="sm"
                  variant="soft"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: link -->
          <template #cell-link="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <a
                v-if="cleanStr(data.link)"
                :href="cleanStr(data.link)"
                target="_blank"
                class="link-icon"
              >
                <i class="far fa-external-link" />
              </a>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: lang -->
          <template #cell-lang="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.lang)"
                class="lang-badge"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: active -->
          <template #cell-active="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_active === '1' || data.is_active === 1"
                v-tooltip.top="t('active')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('inactive')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { findPermission, UserState } from '@/composables/User';
import { ListResult } from '@/composables/GraphQL';
import { ShopsSyncs } from '@/types/generated-types/graphql';
import ShopsSyncsList from '@/composables/shop/ShopSyncs';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'ProductCategoriesV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const synchroData = ref<ListResult<ShopsSyncs>>();
    const idShop = UserState.activeShop?.id ?? 0;
    const launchSynchro = ref(false);

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/products-categories',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'category_id',
        header: '',
        fields: [{
          name: 'category_id',
          label: t('myShopData.product_categories.headers.id_category'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'category_info',
        header: t('myShopData.product_categories.headers.name'),
        fields: [{
          name: 'category_id',
          label: t('id'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'name',
          label: t('myShopData.product_categories.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.product_categories.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.product_categories.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'link',
        header: t('myShopData.product_categories.headers.link'),
        fields: [{
          name: 'link',
          label: t('myShopData.product_categories.headers.link'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.product_categories.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.product_categories.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.product_categories.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.product_categories.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('active'), value: '1' },
              { label: t('inactive'), value: '0' },
            ],
          },
        }],
      },
    ];

    onMounted(async () => {
      synchroData.value = await ShopsSyncsList('productsCategories,all', idShop, 'user');
    });

    const synchroAction = (launch: boolean) => {
      launchSynchro.value = launch;
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO: Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/product-categories',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.product_categories.export.success'));
        // } else {
        //   await showToastError(t('myShopData.product_categories.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.product_categories.export.error'));
      }
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      findPermission,
      handleExport,
      cleanStr,
    };
  },
});
</script>

<style scoped>
/* Category info composite column */
.category-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-info-cell .category-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-info-cell .category-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 3px;
}

.category-info-cell .category-name {
  font-weight: 600;
  color: #1f2937;
}

.category-info-cell .category-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.category-info-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

/* Link icon */
.link-icon {
  color: #94c840;
  transition: color 0.2s ease;
}

.link-icon:hover {
  color: #7aa832;
}

/* Language badge */
.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}
</style>

<style scoped lang="scss">
/* Page layout */
.product-categories-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
